import React, { Component } from "react";
import { Link } from "react-router-dom";
import "animate.css";

import Navbar from "../../Navbar/Navbar";
import MainBodyContainer from "../../Common/Body/MainBodyContainer/MainBodyContainer";
import Footer from "../../Footer/Footer";

import communityImage from "../../../images/programs/women-4-women-community.png";
import "./Women4Women.css";

class Women4Women extends Component {
  constructor(props) {
    super(props);
    this.state = {
      expandedFaq: -1,
      showStickyCta: false,
      activeStage: 0,
      visibleSections: new Set(),
    };
    this.observerRef = null;
    this.sectionRefs = {};
  }

  componentDidMount() {
    window.addEventListener("scroll", this.handleScroll);
    this.setupSectionObserver();
  }

  componentWillUnmount() {
    window.removeEventListener("scroll", this.handleScroll);
    if (this.observerRef) this.observerRef.disconnect();
  }

  setupSectionObserver = () => {
    this.observerRef = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.setState((prev) => ({
              visibleSections: new Set([...prev.visibleSections, entry.target.dataset.section]),
            }));
          }
        });
      },
      { threshold: 0.15 }
    );
    Object.values(this.sectionRefs).forEach((ref) => {
      if (ref) this.observerRef.observe(ref);
    });
  };

  registerSection = (id) => (el) => {
    if (el && !this.sectionRefs[id]) {
      this.sectionRefs[id] = el;
      el.dataset.section = id;
      if (this.observerRef) this.observerRef.observe(el);
    }
  };

  handleScroll = () => {
    const showStickyCta = window.scrollY > 500;
    if (this.state.showStickyCta !== showStickyCta) {
      this.setState({ showStickyCta });
    }
  };

  toggleFaq = (index) => {
    this.setState((prev) => ({
      expandedFaq: prev.expandedFaq === index ? -1 : index,
    }));
  };

  setActiveStage = (index) => {
    this.setState({ activeStage: index });
  };

  isVisible = (id) => this.state.visibleSections.has(id);

  // ─── Content ────────────────────────────────────────────────────────────────

  beliefs = [
    "Every woman's idea deserves to be taken seriously.",
    "No one should have to build in isolation.",
    "Lived experience is as valuable as formal expertise.",
    "Progress — however small — deserves to be celebrated.",
    "Community is infrastructure.",
  ];

  pillars = [
    {
      icon: "🕸️",
      title: "Network & Connect",
      body: "Build real relationships — not transactional networking. Meet women who understand the challenges you face because they've faced them too. Your next collaborator, mentor, or greatest champion may be in the same room.",
      accent: "#9b5e6e",
    },
    {
      icon: "📖",
      title: "Peer & Action Learning",
      body: "Share skills, lived experience and practical solutions through structured peer learning. Everyone teaches, everyone learns. Knowledge flows in both directions — always.",
      accent: "#7a5c8a",
    },
    {
      icon: "🎙️",
      title: "Speakers & Mentors",
      body: "Hear from professionals, established founders and guest experts chosen by the group. Sessions are never imposed — topics and speakers are shaped by the women in the room.",
      accent: "#c9887a",
    },
    {
      icon: "🌱",
      title: "Goals & Accountability",
      body: "Each session closes with SMART commitments. Gentle, loving accountability from women who want you to succeed. Progress is tracked, celebrated, and supported every step of the way.",
      accent: "#6a9a7a",
    },
  ];

  journeyStages = [
    {
      stage: "Seed",
      emoji: "🌱",
      label: "The Seed",
      title: "You have an idea",
      body: "A spark — something you've been turning over in your mind. Maybe a skill you love, a service you know communities need, or a product you haven't yet dared to build. W4W is where that seed finds water.",
      color: "#6a9a7a",
    },
    {
      stage: "Blossom",
      emoji: "🌸",
      label: "The Blossom",
      title: "You're testing the ground",
      body: "You've started. Maybe you've sold a few things, run a workshop, or begun collecting clients. You need honest reflection, peer support and the confidence to keep going. W4W helps you find your footing.",
      color: "#9b5e6e",
    },
    {
      stage: "Grow",
      emoji: "✦",
      label: "In Full Growth",
      title: "You're already trading",
      body: "Your business is real and running. The challenges have changed — pricing, boundaries, marketing, staying energised. W4W is a space to problem-solve with women who get it, and who'll challenge you with love.",
      color: "#c9887a",
    },
    {
      stage: "Soar",
      emoji: "🦋",
      label: "Ready to Soar",
      title: "You're ready to scale",
      body: "You're growing and thinking bigger — new markets, funding, partnerships, team. W4W connects you with mentors and opportunities. And now you give back: your story becomes someone else's permission to begin.",
      color: "#7a5c8a",
    },
  ];

  faqs = [
    {
      question: "Do I need a business already to join?",
      answer:
        "Not at all. W4W welcomes women at every stage — from a quiet idea you haven't said out loud yet, to a business you're already proud of. The only requirement is that you're a woman with something you want to build.",
    },
    {
      question: "Is it really free?",
      answer:
        "Yes — genuinely, completely free. Women 4 Women is run entirely on a volunteer basis. There is no cost to join as a participant, speaker or mentor. We believe access to community should never be a barrier.",
    },
    {
      question: "Who is W4W for?",
      answer:
        "Women at any stage of enterprise, from any background. Mums, career changers, side-hustlers, founders, freelancers — and women who don't yet have a word for what they are, but know they want to build something. You're welcome here.",
    },
    {
      question: "What if I can't make every session?",
      answer:
        "Life happens — especially when you're holding many things at once. You don't need to attend every session to belong. Come when you can, and you'll find the community picks up where you left off.",
    },
    {
      question: "How do I become a speaker or mentor?",
      answer:
        "Get in touch. Whether you're a professional, an established founder or simply someone whose story would light a path for others — we'd love to hear from you. Sessions are shaped by the women in the room.",
    },
    {
      question: "We'd like to support W4W. How can we help?",
      answer:
        "W4W runs entirely on goodwill and volunteer effort. We welcome venue partners, funders, organisations offering pro-bono support, and anyone who believes in what we're building. Please reach out — every form of support is meaningful.",
    },
  ];

  // ─── Render ──────────────────────────────────────────────────────────────────

  render() {
    const { expandedFaq, showStickyCta, activeStage } = this.state;
    const current = this.journeyStages[activeStage];

    return (
      <div className="w4w-page">
        <Navbar />
        <div>
          <MainBodyContainer heading={undefined}>

            {/* ───── HERO ───── */}
            <div className="w4w-hero">
              {/* Falling golden snow particles */}
              <div className="w4w-botanicals" aria-hidden="true">
                <span className="w4w-botanicle b1">✦</span>
                <span className="w4w-botanicle b2">◆</span>
                <span className="w4w-botanicle b3">✦</span>
                <span className="w4w-botanicle b4">◇</span>
                <span className="w4w-botanicle b5">✦</span>
                <span className="w4w-botanicle b6">◆</span>
                <span className="w4w-botanicle b7">✦</span>
                <span className="w4w-botanicle b8">◆</span>
                <span className="w4w-botanicle b9">✦</span>
                <span className="w4w-botanicle b10">◇</span>
                <span className="w4w-botanicle b11">✦</span>
                <span className="w4w-botanicle b12">◆</span>
                <span className="w4w-botanicle b13">✦</span>
                <span className="w4w-botanicle b14">◇</span>
                <span className="w4w-botanicle b15">✦</span>
              </div>

              <div className="w4w-hero-inner">
                <div className="w4w-hero-kicker animate__animated animate__fadeInDown">
                  <span className="w4w-kicker-line"></span>
                  <span>Women 4 Women</span>
                  <span className="w4w-kicker-line"></span>
                </div>

                <h1 className="w4w-hero-headline animate__animated animate__fadeInUp">
                  No woman should have<br />
                  to build <em>alone.</em>
                </h1>

                <p className="w4w-hero-body animate__animated animate__fadeInUp">
                  An inclusive, peer-led community for women at every stage of enterprise — 
                  from a seed of an idea to a business you're proud of. 
                  Come as you are. Leave knowing you're not alone.
                </p>

                <div className="w4w-hero-chips animate__animated animate__fadeInUp">
                  <span className="w4w-chip">✦ Every stage welcome</span>
                  <span className="w4w-chip">✦ Women-led & shaped</span>
                </div>

                <div className="w4w-hero-actions animate__animated animate__fadeInUp">
                  <Link to="/contact?inquiry=women-4-women" className="w4w-btn-primary">
                    I want to join
                    <span className="w4w-btn-arrow">→</span>
                  </Link>
                  <Link to="/contact?inquiry=women-4-women-partner" className="w4w-btn-ghost">
                    Partner with us
                  </Link>
                </div>
              </div>
            </div>

            {/* ───── MANIFESTO STRIP ───── */}
            <div
              className={`w4w-manifesto ${this.isVisible("manifesto") ? "revealed" : ""}`}
              ref={this.registerSection("manifesto")}
            >
              <div className="w4w-manifesto-label">We believe</div>
              <div className="w4w-manifesto-beliefs">
                {this.beliefs.map((b, i) => (
                  <div
                    key={i}
                    className="w4w-belief"
                    style={{ animationDelay: `${i * 0.12}s` }}
                  >
                    <span className="w4w-belief-mark">✦</span>
                    <span>{b}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* ───── ABOUT ───── */}
            <section
              className={`w4w-about ${this.isVisible("about") ? "revealed" : ""}`}
              ref={this.registerSection("about")}
            >
              <div className="w4w-about-grid">
                <div className="w4w-about-text">
                  <span className="w4w-eyebrow">What is W4W?</span>
                  <h2 className="w4w-section-heading">Ideas heard. Progress supported. No one left to build alone.</h2>
                  <p>
                    Women 4 Women is an inclusive, user-led platform — a space that exists because
                    women's ideas too often go unheard, unsupported and unstarted. Not here.
                  </p>
                  <p>
                    Through peer learning, action learning and honest conversation, W4W creates 
                    the conditions for women to take real steps forward — sharing skills and lived 
                    experience with one another, and setting SMART actions with gentle 
                    accountability from women who genuinely want them to succeed.
                  </p>
                  <p>
                    W4W also connects participants to wider opportunities — professionals, 
                    established business owners and guest speakers, whose expertise and stories 
                    open new doors. Sessions are shaped collectively: every topic and every speaker 
                    chosen by the women in the room.
                  </p>
                  <Link to="/contact?inquiry=women-4-women" className="w4w-about-cta">
                    Register your interest <span>→</span>
                  </Link>
                </div>
                <div className="w4w-about-visual">
                  <div className="w4w-image-frame">
                    <img
                      src={communityImage}
                      alt="Two women from different backgrounds in focused, warm conversation — an open notebook between them, other women connecting in the background."
                    />
                    <div className="w4w-image-caption">
                      <span className="w4w-caption-mark">✦</span>
                      Every voice deserves to be heard
                    </div>
                  </div>
                  <div className="w4w-about-stat-block">
                    <div className="w4w-about-stat-divider"></div>
                    <div className="w4w-about-stat">
                      <span className="w4w-as-value">All stages</span>
                      <span className="w4w-as-label">Seed to scale</span>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* ───── PILLARS ───── */}
            <section
              className={`w4w-pillars ${this.isVisible("pillars") ? "revealed" : ""}`}
              ref={this.registerSection("pillars")}
            >
              <div className="w4w-pillars-header">
                <span className="w4w-eyebrow">What W4W offers</span>
                <h2 className="w4w-section-heading centered">
                  Four threads. One woven community.
                </h2>
                <p className="w4w-section-sub">
                  Every element of W4W is intentional. Together they create something 
                  stronger than any one part could be alone.
                </p>
              </div>

              <div className="w4w-pillars-grid">
                {this.pillars.map((pillar, i) => (
                  <div
                    key={i}
                    className="w4w-pillar"
                    style={{
                      "--pillar-accent": pillar.accent,
                      animationDelay: `${i * 0.1}s`,
                    }}
                  >
                    <div className="w4w-pillar-top">
                      <span className="w4w-pillar-icon">{pillar.icon}</span>
                      <span className="w4w-pillar-number">0{i + 1}</span>
                    </div>
                    <h3 className="w4w-pillar-name">{pillar.title}</h3>
                    <p className="w4w-pillar-body">{pillar.body}</p>
                    <div className="w4w-pillar-accent-line"></div>
                  </div>
                ))}
              </div>
            </section>

            {/* ───── JOURNEY ───── */}
            <section
              className={`w4w-journey ${this.isVisible("journey") ? "revealed" : ""}`}
              ref={this.registerSection("journey")}
            >
              <div className="w4w-journey-header">
                <span className="w4w-eyebrow">Wherever you are</span>
                <h2 className="w4w-section-heading centered">
                  Your stage. Your pace. Your community.
                </h2>
                <p className="w4w-section-sub">
                  W4W doesn't require you to be at a certain stage to belong. 
                  Click your stage to see how W4W shows up for you.
                </p>
              </div>

              <div className="w4w-journey-nav">
                {this.journeyStages.map((stage, i) => (
                  <button
                    key={i}
                    className={`w4w-stage-tab ${activeStage === i ? "active" : ""}`}
                    onClick={() => this.setActiveStage(i)}
                    style={{ "--tab-color": stage.color }}
                  >
                    <span className="w4w-stage-tab-emoji">{stage.emoji}</span>
                    <span className="w4w-stage-tab-label">{stage.label}</span>
                  </button>
                ))}
              </div>

              <div
                className="w4w-stage-panel"
                key={activeStage}
                style={{ "--panel-color": current.color }}
              >
                <div className="w4w-stage-panel-emoji">{current.emoji}</div>
                <div className="w4w-stage-panel-text">
                  <span className="w4w-stage-panel-badge">{current.stage}</span>
                  <h3 className="w4w-stage-panel-title">{current.title}</h3>
                  <p className="w4w-stage-panel-body">{current.body}</p>
                  <Link to="/contact?inquiry=women-4-women" className="w4w-stage-panel-cta">
                    This is me — join W4W <span>→</span>
                  </Link>
                </div>
              </div>
            </section>

            {/* ───── HOW IT WORKS ───── */}
            <section
              className={`w4w-how ${this.isVisible("how") ? "revealed" : ""}`}
              ref={this.registerSection("how")}
            >
              <div className="w4w-how-inner">
                <div className="w4w-how-left">
                  <span className="w4w-eyebrow">How a session works</span>
                  <h2 className="w4w-section-heading">
                    A structure built for real women with real lives.
                  </h2>
                  <p>
                    Every W4W session follows a warm, purposeful rhythm — never transactional, 
                    always human. You'll leave with new thinking, real connections, and one 
                    concrete step forward.
                  </p>
                  <div className="w4w-how-cta-wrap">
                    <Link to="/contact?inquiry=women-4-women" className="w4w-btn-primary">
                      Join a session
                      <span className="w4w-btn-arrow">→</span>
                    </Link>
                  </div>
                </div>

                <div className="w4w-how-steps">
                  {[
                    {
                      num: "01",
                      title: "Welcome & Check-In",
                      body: "An honest, open circle. How is everyone really? Wins are named, challenges acknowledged. Every voice heard before anything else.",
                    },
                    {
                      num: "02",
                      title: "Skill Share or Speaker",
                      body: "Practical content chosen by the group — from pricing your services to understanding your finances to hearing from a guest who's been where you want to go.",
                    },
                    {
                      num: "03",
                      title: "Action Learning Sets",
                      body: "In small, confidential groups, you bring a real challenge. The group listens deeply, asks powerful questions, helps you find your own answers. No unsolicited advice. Just presence.",
                    },
                    {
                      num: "04",
                      title: "SMART Commitments",
                      body: "Every session closes with one action — specific, achievable, yours. The group holds it. You return next time to share what happened. Progress is witnessed.",
                    },
                  ].map((step, i) => (
                    <div key={i} className="w4w-step">
                      <div className="w4w-step-num">{step.num}</div>
                      <div className="w4w-step-content">
                        <h4 className="w4w-step-title">{step.title}</h4>
                        <p className="w4w-step-body">{step.body}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* ───── DUAL CTA: JOIN / SPEAK ───── */}
            <section
              className={`w4w-join ${this.isVisible("join") ? "revealed" : ""}`}
              ref={this.registerSection("join")}
            >
              <div className="w4w-join-grid">
                <div className="w4w-join-card w4w-join-participant">
                  <span className="w4w-join-icon">👩‍💼</span>
                  <h3 className="w4w-join-title">Join as a participant</h3>
                  <p className="w4w-join-body">
                    You're a woman with something to build — and you're done doing it alone. 
                    W4W is for you. Every stage welcome. Completely free.
                  </p>
                  <ul className="w4w-join-list">
                    <li><span>✦</span> Any stage of enterprise</li>
                    <li><span>✦</span> No experience required</li>
                    <li><span>✦</span> Free, always</li>
                    <li><span>✦</span> Mum-friendly & inclusive</li>
                  </ul>
                  <Link to="/contact?inquiry=women-4-women" className="w4w-join-btn w4w-join-btn-light">
                    Register my interest <span>→</span>
                  </Link>
                </div>

                <div className="w4w-join-card w4w-join-speaker">
                  <span className="w4w-join-icon">🎙️</span>
                  <h3 className="w4w-join-title">Speak, mentor, or volunteer</h3>
                  <p className="w4w-join-body">
                    You've built something. You've learned hard lessons. Your story and expertise 
                    can open doors for women who need to hear exactly what you know.
                  </p>
                  <ul className="w4w-join-list">
                    <li><span>✦</span> Share your expertise</li>
                    <li><span>✦</span> Topics shaped by the group</li>
                    <li><span>✦</span> Flexible commitment</li>
                    <li><span>✦</span> Volunteer or mentor role</li>
                  </ul>
                  <Link to="/contact?inquiry=women-4-women-speaker" className="w4w-join-btn w4w-join-btn-outline">
                    Get in touch <span>→</span>
                  </Link>
                </div>
              </div>
            </section>

            {/* ───── PARTNER ───── */}
            <section
              className={`w4w-partner ${this.isVisible("partner") ? "revealed" : ""}`}
              ref={this.registerSection("partner")}
            >
              <div className="w4w-partner-inner">
                <div className="w4w-partner-text">
                  <span className="w4w-partner-label">Partner with us</span>
                  <h2 className="w4w-partner-heading">
                    Help us keep the doors open.
                  </h2>
                  <p>
                    Women 4 Women runs entirely on volunteer effort. The mission is real. 
                    The impact is real. The need for sustainable support is real.
                  </p>
                  <p>
                    Women who build businesses become role models — for their children, their 
                    families, their communities. Your investment — whether space, funding, 
                    expertise or advocacy — ripples further than you may know.
                  </p>
                  <div className="w4w-partner-types">
                    {[
                      { icon: "🏢", label: "Venue partner", desc: "Offer your space" },
                      { icon: "💷", label: "Funder or sponsor", desc: "Sustain the programme" },
                      { icon: "🎓", label: "Pro-bono expert", desc: "Share your skills" },
                      { icon: "📣", label: "Stakeholder", desc: "Champion our mission" },
                    ].map((t, i) => (
                      <div key={i} className="w4w-partner-type">
                        <span className="w4w-pt-icon">{t.icon}</span>
                        <div>
                          <strong>{t.label}</strong>
                          <span>{t.desc}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  <Link to="/contact?inquiry=women-4-women-partner" className="w4w-partner-btn">
                    Discuss a partnership <span>→</span>
                  </Link>
                </div>

                <div className="w4w-partner-quote">
                  <div className="w4w-pq-inner">
                    <span className="w4w-pq-mark">"</span>
                    <p className="w4w-pq-text">
                      Women supporting one another to build income and grow businesses — 
                      becoming positive role models for their children, their families, 
                      and their communities.
                    </p>
                    <div className="w4w-pq-divider"></div>
                    <span className="w4w-pq-source">The W4W mission</span>
                  </div>
                </div>
              </div>
            </section>

            {/* ───── FAQ ───── */}
            <section
              className={`w4w-faq ${this.isVisible("faq") ? "revealed" : ""}`}
              ref={this.registerSection("faq")}
            >
              <div className="w4w-faq-header">
                <span className="w4w-eyebrow">Common questions</span>
                <h2 className="w4w-section-heading centered">Everything you need to know.</h2>
              </div>
              <div className="w4w-faq-list">
                {this.faqs.map((faq, i) => (
                  <div
                    key={i}
                    className={`w4w-faq-item ${expandedFaq === i ? "open" : ""}`}
                  >
                    <button
                      className="w4w-faq-trigger"
                      onClick={() => this.toggleFaq(i)}
                    >
                      <span className="w4w-faq-q">{faq.question}</span>
                      <span className="w4w-faq-chevron">{expandedFaq === i ? "−" : "+"}</span>
                    </button>
                    {expandedFaq === i && (
                      <div className="w4w-faq-answer">
                        <p>{faq.answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>

            {/* ───── FINAL CTA ───── */}
            <section
              className={`w4w-final ${this.isVisible("final") ? "revealed" : ""}`}
              ref={this.registerSection("final")}
            >
              <div className="w4w-final-threads" aria-hidden="true">
                <svg viewBox="0 0 800 300" preserveAspectRatio="none">
                  <path className="w4w-ft-path ft1" d="M0,100 Q200,30 400,150 Q600,260 800,80" />
                  <path className="w4w-ft-path ft2" d="M0,200 Q200,130 400,220 Q600,300 800,160" />
                </svg>
              </div>
              <div className="w4w-final-content">
                <span className="w4w-final-mark">✦</span>
                <h2 className="w4w-final-heading">
                  Your seat at the table<br /><em>is waiting.</em>
                </h2>
                <p className="w4w-final-body">
                  Women 4 Women is free, inclusive, and shaped by you. 
                  Register your interest and we'll be in touch with session details.
                </p>
                <div className="w4w-final-actions">
                  <Link to="/contact?inquiry=women-4-women" className="w4w-btn-primary large">
                    I want to join
                    <span className="w4w-btn-arrow">→</span>
                  </Link>
                  <Link to="/contact" className="w4w-final-ghost">
                    Ask a question
                  </Link>
                </div>
              </div>
            </section>

            {/* ───── STICKY BAR ───── */}
            {showStickyCta && (
              <div className="w4w-sticky">
                <span className="w4w-sticky-label">Women 4 Women ✦ Free to join</span>
                <Link to="/contact?inquiry=women-4-women" className="w4w-sticky-btn">
                  Register interest →
                </Link>
              </div>
            )}

          </MainBodyContainer>
        </div>
        <Footer />
      </div>
    );
  }
}

export default Women4Women;
